import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Form, FormGroup, ControlLabel, FormControl } from 'react-bootstrap';
import { useRequestData } from 'wsm-common-data';
import { formatFacetValue } from 'wsm-srp-utilities';

const RangeInput = ({
	id,
	minValue,
	maxValue,
	minInput,
	maxInput,
	minLabel,
	maxLabel,
	handleSubmit,
	isCurrency
}) => {
	const [currentMinVal, setCurrentMin] = useState(minInput);
	const [currentMaxVal, setCurrentMax] = useState(maxInput);
	const { locale } = useRequestData();

	const isNumber = (num) => !Number.isNaN(parseInt(num, 10));
	const getNumberFromInput = (str) =>
		parseInt(str.replace(/[^0-9]/g, ''), 10);

	useEffect(() => {
		setCurrentMin(minInput);
		setCurrentMax(maxInput);
	}, [minInput, maxInput]);

	const handleFormSubmit = () => {
		if (isNumber(currentMinVal) && isNumber(currentMaxVal)) {
			handleSubmit(currentMinVal, currentMaxVal, 'input');
		}
	};

	const handleRangeUpdate = (e, type) => {
		const number = getNumberFromInput(e.target.value);
		const isWithinRange =
			number >= minValue &&
			number <= maxValue &&
			((type === 'min' && number <= maxInput) ||
				(type === 'max' && number >= minInput));
		if (isWithinRange) {
			if (type === 'min') {
				setCurrentMin(number);
			} else if (type === 'max') {
				setCurrentMax(number);
			}
		} else {
			setCurrentMin(minInput);
			setCurrentMax(maxInput);
		}

		handleFormSubmit();
	};

	const handleKeyDown = (e, type) => {
		if (e.keyCode === 13 && !e.shiftKey) {
			handleRangeUpdate(e, type);
		}
	};

	const handleChange = (e, type) => {
		const number = getNumberFromInput(e.target.value);

		if (number) {
			if (type === 'min') {
				setCurrentMin(number);
			} else if (type === 'max') {
				setCurrentMax(number);
			}
		} else if (type === 'min') {
			setCurrentMin(e.target.value);
		} else if (type === 'max') {
			setCurrentMax(e.target.value);
		}
	};

	const minInputValidation = isNumber(currentMinVal) ? null : 'error';
	const maxInputValidation = isNumber(currentMaxVal) ? null : 'error';

	return (
		<Form className="row">
			<FormGroup
				validationState={minInputValidation}
				id={`${id}-group-min`}
				controlId={`${id}-min`}
				className="col-xs-12 col-sm-6"
			>
				<ControlLabel
					htmlFor={`${id}-min`}
					className="text-neutral-400"
				>
					{minLabel}
				</ControlLabel>
				<FormControl
					id={`${id}-min`}
					value={formatFacetValue(
						id,
						isCurrency,
						currentMinVal,
						locale
					)}
					onChange={(e) => handleChange(e, 'min')}
					onBlur={(e) => handleRangeUpdate(e, 'min')}
					onKeyDown={(e) => handleKeyDown(e, 'min')}
				/>
			</FormGroup>
			<FormGroup
				validationState={maxInputValidation}
				id={`${id}-group-max`}
				controlId={`${id}-max`}
				className="col-xs-12 col-sm-6"
			>
				<ControlLabel
					htmlFor={`${id}-max`}
					className="text-neutral-400"
				>
					{maxLabel}
				</ControlLabel>
				<FormControl
					id={`${id}-max`}
					value={formatFacetValue(
						id,
						isCurrency,
						currentMaxVal,
						locale
					)}
					onChange={(e) => handleChange(e, 'max')}
					onBlur={(e) => handleRangeUpdate(e, 'max')}
					onKeyDown={(e) => handleKeyDown(e, 'max')}
				/>
			</FormGroup>
		</Form>
	);
};

RangeInput.propTypes = {
	id: PropTypes.string.isRequired,
	minValue: PropTypes.number.isRequired,
	maxValue: PropTypes.number.isRequired,
	minInput: PropTypes.number.isRequired,
	maxInput: PropTypes.number.isRequired,
	minLabel: PropTypes.string.isRequired,
	maxLabel: PropTypes.string.isRequired,
	handleSubmit: PropTypes.func.isRequired,
	isCurrency: PropTypes.bool.isRequired
};

export default RangeInput;
